<template>
  <div id="app">
    <Navbar />
    <router-view />
    <ModalHost />
    <Footer />
  </div>
</template>

<script setup>
import { useThemeStore } from './stores/themeStore'
import Navbar from './components/Navbar.vue'
import Footer from './components/Footer.vue'
import ModalHost from '@/components/Modal/ModalHost.vue'
import { onMounted } from 'vue'
import { useUserStore } from './stores/userStore'
import { useLocaleStore } from '@/stores/localeStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useCountriesStore } from '@/stores/countriesStore'

const settingsStore = useSettingsStore()
const countriesStore = useCountriesStore()
const userStore = useUserStore()
const themeStore = useThemeStore()
const localeStore = useLocaleStore()

onMounted(async () => {
  // Chargement en parallèle : settings et pays
  await Promise.all([
    settingsStore.fetchSettings(),
    countriesStore.fetchCountries(),
  ])

  themeStore.initTheme()
  localeStore.restoreDefaultCountry()

  await userStore.fetchUser()

  if (!localeStore.resolved) {
    await localeStore.setFromIp()
  }
})
</script>

<style>
/* Page */
.page-container {
  display: flex;
  flex-direction: column;
  margin: auto;
  padding: 2rem 2rem 4rem;
  min-height: 45.4vh;
  /* max-width: 72.2rem; */
  /* flex: 1; */
}

.page-header {
  padding: 2rem 0 3rem;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* .page-header h1:not(:has(+p)) { */
/*   padding-bottom: 3rem; */
/* } */
.page-header p {
  font-size: 1.2rem;
  color: var(--color-text-secondary);
}

.page-header.center h1 {
  text-align: center;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 4rem;
  align-items: center;
}

.page-content article {
  gap: 4rem;
}

article h2 {
  margin: 0.5rem auto 1rem;
  color: var(--color-jungle);
  line-height: 1.2;
}

article {
  display: flex;
  flex-direction: row;
  gap: 5.0rem;
  justify-content: center;
  align-items: flex-start;
}

article:nth-child(even) img {
  order: 1;
}

article {
  font-family: var(--font-family-text);
  color: var(--color-jungle);
  font-size: 1.1rem;
  line-height: 1.8;
}

article p {
  text-align: justify;
  margin-bottom: 1.2rem;
}

article ul {
  list-style-type: none;
  margin: 0;
}

article img {
  height: auto;
  max-width: 30%;
}

.logo {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.logo img {
  height: 60px;
  width: auto;
}

/* Hero */
.hero {
  background: linear-gradient(135deg,
      var(--color-jungle) 0%,
      var(--color-feuille) 100%);
  color: var(--bg-primary);
  padding: 4rem 1rem;
  text-align: center;
  width: 100%;
  margin-bottom: 2rem;
}

.hero-content h1 {
  font-size: 3.2rem;
  font-family: var(--font-family-title);
}

.hero-content h1>strong {
  text-transform: uppercase;

}

.hero-content p {
  font-size: 1.2rem;
  font-family: var(--font-family-text);
}

/* Form elements */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

.form-group .btn-group {
  margin-top: 1.5rem;
}

.form-group label:not(.checkbox-label) {
  font-size: 0.9rem;
  font-family: var(--font-family-text);
  color: var(--color-text-primary);
  margin: 1rem 0 0.2rem;
  text-align: left;
}

.form-group input,
.form-group select,
.form-group textarea {
  background-color: var(--bg-primary);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--color-border);
  font-family: var(--font-family-text);
  padding: 0.75rem;
  font-size: 1rem;
  transition: border 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  width: 100%;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-feuille);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-group select input:disabled,
select:disabled,
textarea:disabled {
  background-color: var(--bg-black-alpha);
  cursor: not-allowed;
}

input.success {
  border: 2px solid var(--color-espoir);
}

input.error {
  border: 2px solid var(--color-terracotta) !important;
}

input.disabled {
  background-color: var(--bg-black-alpha);
  cursor: not-allowed;
  color: var(--color-text-secondary);
}

select,
input {
  background-color: var(--bg-primary);
  color: var(--color-text-primary);
}

.btn {
  border-radius: var(--border-radius-sm) !important;
  cursor: pointer;
  width: fit-content;
  font-size: 1rem;
  font-family: var(--font-family-text);
  font-weight: 600;
  white-space: nowrap;
  padding: 0.75rem 1.5rem;
  border: none;
  transition: box-shadow 0.3s, background-color 0.3s;
}

.btn:hover {
  box-shadow: var(--shadow-xs);
}

.btn:active {
  opacity: 0.9;
}

.btn-primary {
  color: var(--bg-primary);
  background-color: var(--color-feuille);
  font-weight: 600;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-jungle);
}

.btn:disabled,
.btn-primary:disabled,
.btn-danger:disabled,
.btn-secondary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.4rem 0.6rem;
  background-color: var(--color-grey-light);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-grey-medium);
  font-size: 0.9rem;
}

.btn-secondary:hover {
  background-color: var(--color-grey-medium);
}

.btn-danger {
  background-color: var(--color-terracotta);
  color: var(--bg-primary);
  font-weight: 600;
}

.btn-danger:hover:not(:disabled) {
  background-color: var(--color-burgundy);
}

.btn-warning {
  background-color: var(--color-eclat);
  color: var(--color-text-primary);
  font-weight: 600;
  border: 1px solid var(--color-amber);
}

.btn-warning:hover:not(:disabled) {
  background-color: var(--color-amber);
}

.btn-full {
  width: 100%;
}

.btn-download-app img {
  /* height: 60px; */
  max-width: 15rem;
  transition: transform 0.3s ease;
}

.btn-download-app:hover img {
  transform: translateY(-3px);
}

.icon-btn {
  border: none;
  background: transparent;
  width: 1.5rem;
  height: 1.5rem;
  font-size: 1.2rem;
  transition: text-shadow 0.2s;
  margin: 0 0.2rem;
}

.icon-btn:hover {
  cursor: pointer;
  text-shadow: var(--shadow-md);
}

.danger {
  color: var(--color-terracotta);
}

.btn-success,
.btn-save {
  background-color: var(--color-pastel-green);
  color: var(--color-jungle);
  font-weight: 600;
  border: 1px solid var(--color-jungle);
}

.btn-success:hover,
.btn-save:hover {
  background-color: var(--color-feuille);
  color: var(--bg-primary);
}

.btn-success:disabled:hover,
.btn-save:disabled:hover {
  background-color: var(--color-pastel-green);
  box-shadow: none;
  color: var(--color-jungle);
}

.btn-danger-secondary {
  color: var(--color-terracotta);
  border: 1px solid var(--color-terracotta);
  background-color: var(--color-pastel-red);
}

.btn-danger-secondary:hover {
  color: var(--bg-primary);
  border-color: var(--color-burgundy);
  background-color: var(--color-terracotta);

  /* --badge-paid-bg: #d4edda; */
}

/* .btn-icon { */
/*   background: var(--color-grey-light); */
/*   border: 1px solid var(--color-border); */
/*   width: 1.5rem; */
/*   height: 1.5rem; */
/*   padding: 0; /* important */
/*   display: flex; */
/*   align-items: center; */
/*   justify-content: center; */
/*   cursor: pointer; */
/*   font-size: 1.2rem; */
/*   color: var(--color-text-secondary); */
/*   transition: color 0.2s; */
/*   border-radius: 50%; */
/*   margin: 0.1rem; */
/* } */
/* .btn-primary.is-highlight { */
/*   background: var(--color-feuille); */
/*   color: var(--bg-primary); */
/*   cursor: pointer; */
/* } */
/* .btn-primary.is-highlight:hover { */
/*   filter: brightness(0.95); */
/* } */
/* .btn--save { */
/*   padding: 0.6rem 1.2rem; */
/*   font-size: var(--fs-xs); */
/*   background-color: blue; */
/* } */
/**/
/* .btn--save[disabled] { */
/*   background: var(--admin-disabled-bg); */
/*   color: var(--admin-muted); */
/*   cursor: not-allowed; */
/*   box-shadow: none; */
/* } */
/**/
/* .btn--save.is-highlight { */
/*   background: var(--c-primary); */
/*   color: var(--white); */
/*   cursor: pointer; */
/*   box-shadow: var(--admin-save-shadow); */
/* } */
/* .btn--save.is-highlight:hover { */
/*   filter: brightness(0.95); */
/* } */
/* .btn-dismiss { */
/*   background: none; */
/*   border: none; */
/*   color: var(--c-danger); */
/*   font-size: 1.2rem; */
/*   cursor: pointer; */
/*   padding: 0 0.5rem; */
/*   line-height: 1; */
/*   opacity: 0.7; */
/*   transition: opacity 0.2s; */
/* } */
/* .btn-dismiss:hover { */
/*   opacity: 1; */
/* } */

/* Card */
.card {
  padding: 2rem;
  background-color: var(--bg-primary);
  border: 2px solid var(--bg-primary);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-flow: column;
  width: 100%;
  justify-content: space-between;
}

.card:has(form) {
  border-radius: var(--border-radius-lg);
}

.card:has(table) {
  padding: 0.5rem;
}

.card:hover,
.card:focus,
.card:focus-within {
  border-color: var(--color-jungle);
  box-shadow: var(--shadow-lg);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.card.modal {
  box-shadow: var(--shadow-xl);
}

.card.classic {
  box-shadow: none;
  border: 1px solid var(--color-border);
}

.card p {
  /* text-align: justify; */
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
}

.card-header h2 {
  margin: 0;
  font-family: var(--font-family-title);
  color: var(--color-jungle);
  font-size: 1.5rem;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
}

.card-admin {
  box-shadow: none;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-xs) !important;
}

.card-admin:hover {
  border-color: var(--color-border);
}

.card.card-admin:focus-within {
  border-color: var(--color-border);
}

.card.card-admin.is-modified {
  border-color: var(--color-feuille);
  box-shadow: var(--shadow-md);
}

.card-admin.card-stat {
  padding: 1.5rem;
  justify-content: flex-start;
  align-content: flex-start;
}

.card-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.card-group .card-content,
.card-group .card-footer {
  padding: 0;
}

.card-admin>.card-footer>.btn {
  padding: 0.3rem 1rem;
  display: inline-flex;
  /* ou flex */
  align-items: center;
  /* centre verticalement */
  justify-content: center;
  /* centre horizontalement */
  gap: 0.4rem;
  /* espace entre l’icône et le texte */
  /* color: var(--font-color-text); */
  font-size: 0.9rem;
  font-weight: 500;
}

/* admin chelou */
.card-content label:not(.checkbox-label),
.filters label {
  display: block;
  font-size: 0.8rem !important;
  font-weight: 600;
  color: var(--admin-label-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-content label.checkbox-label {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  gap: 0.5rem;
}

.card-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.card-item:nth-child(even) {
  background-color: var(--color-grey-light);
}

.card-item.card-item--last {
  /* border-top: 1px solid var(--color-border); */
  border-bottom: none;
}

.card-item strong {
  font-weight: 600;
  color: var(--color-text-primary);
}

.card-item span {
  text-align: right;
}

.card.trip {
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid var(--color-border);
  box-shadow: var(--shadow-xs);
  transition: box-shadow 0.2s, border-color 0.2s;
}

.card.trip:hover {
  box-shadow: var(--shadow-sm);
  border-color: var(--color-feuille);
}

.card.rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: none !important;
}

.help-text {
  font-style: italic !important;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  margin-top: 0;
  margin-bottom: 0.5rem;
}

fieldset {
  border: 1px solid var(--color-gray-300, #d1d5db);
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
}

legend {
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0 0.5rem;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.95rem;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.checkbox-label span {
  user-select: none;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pill {
  display: inline-block;
  padding: 0.4rem 0.8rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pill--soon {
  background: var(--badge-refunded-bg);
  color: var(--badge-refunded-text);
  font-style: italic;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.card.card-details {
  border: 2px solid var(--color-border);
  overflow: hidden;
  padding: 0;
}

.card-control {
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--b3);
  border-radius: var(--r4);
  font-size: var(--fs-m);
  background: var(--white);
  color: var(--text-color-primary);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.card-control:focus {
  outline: none;
  border-color: var(--c-primary);
  box-shadow: var(--shadow-focus);
}

.card-control--mono {
  font-family: monospace;
  color: var(--admin-label-color);
}

.preview {
  /* margin: 0.75rem 0rem 1rem 0.75rem; */
  margin: 1rem 0;
  padding: 0.75rem;
  background: var(--color-grey-light);
  border-left: 3px solid var(--color-feuille);
  border-radius: var(--border-radius-sm);
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--color-grey-dark);
}

.preview__line {
  margin: 0.2rem 0;
}

.preview__muted {
  font-style: italic;
  color: #888;
}

.preview img {
  max-height: 20vh;
  max-width: 75%;
  width: auto;
}

.preview:has(img) {
  display: flex;
  flex-direction: row;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: flex-end;
}

.preview:has(img) button {
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--color-burgundy);
  background-color: var(--color-pastel-red);
  color: var(--color-burgundy);
  padding: 0.35rem 0.50rem;
}

.preview-actions {
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-end;
  margin-top: 0.5rem;
}

.preview-actions span {
  flex: 1;
}

.preview-actions button {
  order: 3;
}

.help {
  cursor: help;
}

.mono {
  font-family: monospace !important;
  font-size: 14px !important;
}

.muted {
  color: var(--muted);
}

.roles>.badge {
  margin-right: 0.3rem;
}

.results-count {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 1rem auto;
  min-height: 1.4rem;
  width: 100%;
}

/* Buttons */
.btn-group {
  display: flex;
  flex-flow: row wrap;
  gap: 1rem;
  align-items: center;
}

.btn-group:has(.btn-download-app) {
  justify-content: center;
}

.btn-group.center {
  justify-content: center;
}

/* Download App Buttons */
img.btn-download-app {
  height: 60px;
  width: auto;
  transition: transform 0.3s ease;
}

img.btn-download-app:hover {
  transform: translateY(-3px);
}

/* Table informations */
.table-primary {
  width: 100%;
  border-collapse: collapse;
  /* margin-bottom: 1rem; */

}

.table-primary .btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.9rem;
  transition: opacity 0.3s;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  transition: background-color 0.3s, box-shadow 0.3s;
}

.table-primary th,
.table-primary td {
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  text-align: left;
}

.table-primary th {
  background-color: var(--color-grey-light);
}

.table-primary th.section-header-left {
  font-weight: bold;
  background-color: var(--color-grey-medium);
}

.table-primary th.price-header {
  background-color: var(--color-eclat);
}

.table-primary td.price-value {
  font-weight: bold;
  font-size: 1.2rem;
  color: var(--color-terracotta);
  text-align: right;
}

.table-primary select,
.table-primary input {
  border: none;
  padding: 0.35rem;
  border-radius: var(--border-radius-sm);
  background-color: var(--bg-black-alpha);
}

.table-primary tr td.passenger-row {
  border-left: none;
  border-bottom: none !important;
  padding-left: 0.8rem;
}

.table-primary .is-first-passenger td.passenger-row {
  border-top: none;
}

.table-secondary {
  width: 100%;
  border-collapse: collapse;
}

.table-secondary th,
.table-secondary td {
  padding: 0.35rem;
  text-align: left;
  border: 1px solid var(--color-border);
}

.table-secondary th.left {
  width: 30%;
  text-decoration: none !important;
  font-weight: normal;
}

.table-secondary td {
  width: auto;
}

.table-secondary .header {
  background-color: var(--color-grey-light);
  text-transform: uppercase;
}

.table-secondary .left {
  /* background-color: var(--color-grey-light); */
}

/* .table-primary th { */
/*   background-color: var(--color-grey-light); */
/* } */
/* .table-primary th.section-header-left { */
/*   font-weight: bold; */
/*   background-color: var(--color-grey-medium); */
/* } */
/* .table-primary th.price-header { */
/*   background-color: var(--color-eclat); */
/* } */



/* Toggle Button */
button.toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.toggle-icon {
  display: flex;
  align-items: center;
  color: var(--color-feuille);
}

.toggle-track {
  width: 42px;
  height: 22px;
  background-color: var(--color-grey-medium);
  border-radius: 999px;
  position: relative;
  transition: background-color 0.25s ease;
}

.toggle-track.is-active {
  background-color: var(--color-jungle);
}

.toggle-thumb {
  width: 18px;
  height: 18px;
  background-color: var(--bg-primary);
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.25s ease;
}

.toggle-thumb.is-active {
  transform: translateX(20px);
}

/* Badges */
.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.badge.badge-info {
  background: var(--color-pastel-blue);
  color: var(--color-blue);
}

.badge.badge-pending-payment {
  background: var(--badge-pending-bg);
  color: var(--badge-pending-text);
}

.badge.badge-paid {
  background: var(--badge-paid-bg);
  color: var(--badge-paid-text);
}

.badge.badge-validated {
  background: var(--badge-validated-bg);
  color: var(--badge-validated-text);
}

.badge.badge-cancelled,
.badge.badge-refunded {
  background: var(--badge-cancelled-bg);
  color: var(--badge-cancelled-text);
}

.badge.badge-used {
  background: var(--badge-refunded-bg);
  color: var(--badge-refunded-text);
}

.badge.badge-neutral {
  background: var(--badge-neutral-bg);
  color: var(--badge-neutral-text);
  text-transform: none;
  letter-spacing: 0;
}

.badge.badge-verified {
  background: var(--color-pastel-green);
  color: var(--color-green);
}

.badge.badge-unverified {
  background: var(--badge-pending-bg);
  ;
  color: var(--badge-pending-text);
}

.badge.badge-admin {
  background: var(--color-pastel-red);
  color: var(--color-red);
}

.badge.badge-driver {
  background: var(--color-pastel-blue);
  color: var(--color-blue);
}

.badge.badge-user {
  background: var(--color-pastel-grey);
  color: var(--color-grey-solid);
}

.badge.badge-seller {
  background: var(--color-pastel-yellow);
  color: var(--color-yellow);
}

.dev-badge {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
  animation: pulse 2s ease-in-out infinite;
  z-index: 100;
}

/* Les filtres */
.filters {
  padding: 1rem;
  margin: 0;
  border-radius: var(--border-radius-md) !important;
  background: var(--color-grey-light) !important;
  width: 100%;
  display: flex;
  align-items: flex-start;
}

.filters.form-group {
  gap: 1rem;
}

.filters .btn-group {
  width: 100%;
  margin: 0;
  align-content: stretch;
}

.filters .btn-group.sb {
  align-items: flex-start;
  justify-content: space-between;
}

.filters .btn-group:first-child {
  align-items: flex-start;
  justify-content: space-between;
  /* padding-bottom: 1rem; */
  /* border-bottom: 1px solid var(--color-border); */
}

.filters .btn-group>div {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
}

/* .filters .btn-group:last-child > div { */
/*   flex-flow: column wrap; */
/*   flex: 1; */
/* } */
.filters label {
  margin: 0;
}

/* .filters .btn.btn-chip { */
.btn.btn-chip {
  padding: 0.3rem 1rem;
  font-size: 0.9rem;
  background-color: var(--bg-primary);
  color: var(--color-text-secondary);
  border: 2px solid var(--color-border);
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
}

/* .filters .btn.btn-chip:hover { */
.btn.btn-chip:hover {
  color: var(--color-feuille);
  border-color: var(--color-feuille);
  box-shadow: none;
}

/* .filters .btn.btn-chip.is-active { */
.btn.btn-chip.is-active {
  background-color: var(--color-feuille);
  color: var(--bg-primary);
  border-color: var(--color-feuille);
  box-shadow: var(--shadow-sm);
}

.filters .btn.btn-ghost {
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  padding: 0.3rem 0.6rem;
  font-size: 0.9rem;
}

.filters .btn.btn-ghost:hover {
  background-color: var(--color-grey-medium);
  color: var(--bg-primary);
  box-shadow: none;
}

.filters .btn-group select,
.filters .btn-group input {
  padding: 0.4rem;
}

/* Listing (des tickets, par exemple) */
.listing {
  width: 100%;
  border-radius: var(--border-radius-md);
  overflow: hidden;
  background: var(--bg-primary);
  border: 1px solid var(--color-border);
}

.listing .listing__row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: background-color 0.2s;
}

.listing .listing__row:last-child {
  border-bottom: 0;
}

.listing .listing__row:hover {
  /* background: var(--admin-bg-hover); */
  background: var(--bg-black-alpha);
}

.listing .listing__row.is-validated {
  /* background-color: var(--color-grey-medium); */
  opacity: 0.6;
}

.listing .listing__cell {
  min-width: 0;
}

.listing .listing__cell--shrink {
  flex: 0 0 auto;
  white-space: nowrap;
  overflow: hidden;
}

.listing .listing__cell--fit {
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.listing .listing__cell--grow {
  flex: 1 1 0;
  min-width: 0;
}

.listing .listing__cell--status {
  flex: 0 1 auto;
}

.listing .listing__cell--date {
  flex: 0 1 auto;
  min-width: 0;
}

.listing .listing__cell--route {
  flex: 2;
}

.listing .listing__cell--phone {
  flex: 1;
  text-overflow: ellipsis;
}

/* Navigation par onglets */
.tabs-nav {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid var(--color-grey-light);
  margin-top: 1rem;
  width: 100%;
}

.tabs-nav__link {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  text-decoration: none;
  color: var(--color-text-secondary);
  font-weight: 500;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s ease;
}

.tabs-nav__link:hover,
.tabs-nav__link.is-active:hover {
  color: var(--color-feuille);
  border-bottom-color: var(--color-feuille);
}

.tabs-nav__link.is-active {
  color: var(--color-jungle);
  border-bottom-color: var(--color-jungle);
  background-color: var(--color-grey-light);
}

.tabs-nav .icon {
  font-size: 1.2rem;
  margin-right: 0.5rem;
}

/* Les en vrac */
.group-rows {
  display: flex;
  flex-flow: row wrap;
  gap: 1rem;
  width: 100%;
}

.filters .group-rows label:first-child {
  margin-top: 0;
  flex: 1 1 100%;
}

.filters .group-rows select {
  width: auto;
}

.group-columns {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.flex-mini {
  flex: 0 0 auto;
}

.flex-auto {
  flex: auto;
}

.flex-1 {
  flex: 1;
}

.flex-2 {
  flex: 2;
}

.flex-3 {
  flex: 3;
}

.facultatif {
  font-style: italic;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.validation-code-mini {
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  letter-spacing: 0.1em;
}

.no-mg {
  margin: 0 !important;
}

.no-pd {
  padding: 0 !important;
}

.align-right {
  text-align: right !important;
  justify-self: flex-end !important;
  align-items: flex-end !important;
  justify-content: flex-end !important;
  flex: 0 0 auto !important;
}

/* Fix pour input date sur iOS (tous navigateurs) */
.date-group {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.date-group input[type="date"] {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: block;
  margin: 0;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  background-color: var(--bg-primary);
  color: var(--color-text-primary);
  font-family: var(--font-family-text);
  font-size: 1rem;
  transition: border 0.2s, box-shadow 0.2s;

  /* Force le style textfield sur iOS */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Focus state pour input date */
.date-group input[type="date"]:focus {
  outline: none;
  border-color: var(--color-feuille);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Restaure l'icône calendrier native */
.date-group input[type="date"]::-webkit-calendar-picker-indicator {
  opacity: 1;
  cursor: pointer;
}

/* --------- Dropdown Suggestions (générique) --------- */
.dropdown-trigger {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-xsm);
  color: var(--color-feuille);
  cursor: pointer;
  font-family: var(--font-family-text);
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  white-space: nowrap;
  width: 100%;
  text-align: left;
}

.dropdown-trigger:hover {
  border-color: var(--color-jungle);
  color: var(--color-jungle);
}

.dropdown-trigger:focus {
  outline: none;
  border-color: var(--color-feuille);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Desktop : dropdown plus large que le trigger */
.country-suggestions {
  min-width: 120px;
  /* ajuste selon goût */
  width: max-content;
}

/* Mobile : dropdown forcée à la même largeur que le trigger */
.sidebar-country .suggestions {
  width: 100%;
  min-width: unset;
}

/* --------- Suggestions List --------- */
.suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  list-style: none;
  background: var(--bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-xsm);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: var(--shadow-xs);
  margin: 0;
  padding: 0;
}

.suggestions li {
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
  font-family: var(--font-family-text);
  font-size: 0.9rem;
  max-height: 200px;
}

.suggestions li:hover,
.suggestions li.active {
  background-color: var(--bg-black-alpha);
  color: var(--color-feuille);
}

.suggestions li.active {
  font-weight: 700;
}

/* --------- Wrapper (pour position: relative) --------- */
.dropdown-wrapper {
  position: relative;
  /* width: 100%; */
}

.dropdown-wrapper input {
  box-sizing: border-box;
  /* width: 100%; */
}

.dropdown-wrapper input:focus {
  border-radius: var(--border-radius-sm) var(--border-radius-sm) var(--border-radius-xsm) var(--border-radius-xsm);
}

.carousel-container-trips,
.carousel__item {
  width: 100%;
  min-width: 100px;
  padding: 0 2rem 2rem;
}

.carousel__icon {
  color: var(--color-jungle);
}

.carousel__pagination-button {
  background-color: var(--color-grey-medium) !important;
}

.carousel__pagination-button--active {
  background-color: var(--color-jungle) !important;
}

.carousel__slide .card.trip {
  margin: 6px;
}

/* Base */
#app {
  min-height: 100vh;
  /* pour que le flex remplisse tout l'écran */
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--font-family-text);
  background-color: var(--bg-primary);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  transition: background-color 0.3s ease, color 0.3s ease;
}

h1 {
  font-size: 3rem;
  font-family: var(--font-family-title);
}

h2 {
  font-size: 2.5rem;
  font-family: var(--font-family-title);
}

a {
  color: var(--color-jungle);
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}

a:hover {
  color: var(--color-feuille);
}

a:visited {
  color: var(--color-jungle);
}

/* Search Box */
.search-box-container {
  margin-top: 2rem;
  display: inline-block;
  width: 100%;
  max-width: 1200px;
}

.no-shadow {
  box-shadow: none;
  text-shadow: none;
}

.no-shadow:hover {
  box-shadow: none;
  text-shadow: none;
}

.disabled {
  cursor: not-allowed !important;
  opacity: 0.6;
  transition: none;
  transform: translateY(0) !important;
}

.disabled:hover {
  /* cursor: disabled; */
  /* pointer-events: none; */
  opacity: 0.6;
  transform: translateY(0) !important;
}

.empty-state-content.welcome img {
  min-width: 180px;
  max-width: 20%;
  margin-bottom: 3rem;
  opacity: 0.8;
}

.empty-state-content.welcome h3,
.faq {
  flex: 1 1 100%
}

.btn-loader {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.theme-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 5px;
}

/* icône */
.theme-icon {
  display: flex;
  align-items: center;
  color: var(--color-feuille);
}

.toggle-track {
  width: 42px;
  height: 22px;
  background-color: var(--color-grey-medium);
  border-radius: 999px;
  position: relative;
  transition: background-color 0.25s ease;
}

/* 🌙 mode dark */
.toggle-track.dark {
  background-color: var(--color-jungle);
}

.toggle-thumb {
  width: 18px;
  height: 18px;
  background-color: var(--bg-primary);
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.25s ease;
}

.toggle-thumb.dark {
  transform: translateX(20px);
}

@media (min-width: 768px) {
  .trip-group {
    padding-top: 1rem;
  }

  .date-group .page-header:first-of-type {
    padding-top: 3rem;
  }

}

@media (max-width: 768px) {
  .hero {
    padding: 2.5rem 1rem;
  }

  .hero-content {
    margin: 0 0.5rem;
  }

  .hero-content h1 {
    line-height: 1.2;
    margin-bottom: 1rem;
  }

  .hero-content p {
    font-size: 1.1rem;
  }

  .page-container {
    padding: 1rem 1.5rem 2rem;
  }

  h1 {
    font-size: clamp(1.5rem, 10vw, 2.8rem) !important;
  }

  article h2 {
    font-size: clamp(1.3rem, 8vw, 2rem) !important;
  }

  article p {
    font-size: 1rem;
  }

  article {
    flex-flow: column wrap;
    gap: 1rem;
    align-items: center;
  }

  article img {
    max-width: 80% !important;
    margin: 0 !important;
  }

  article:nth-child(even) img {
    order: 0;
  }

  .listing .listing__cell--status {
    flex: none;
  }

  .listing .listing__cell--date {
    flex: none;
  }

  .listing .listing__cell--route {
    flex: 2;
  }

  .tabs-nav__link {
    flex: 1;
  }

  /* .btn-download-app img {
    height: 50px;
  } */
  /* a controller */
  .page-header h1 {
    line-height: 1.2;
  }

  .tabs-nav .icon {
    margin: 0;
  }

  .tabs-nav__link {
    text-indent: -9999px;
    overflow: hidden;
    white-space: nowrap;
    justify-content: center;
  }

  .tabs-nav__link span {
    text-indent: 0;
    display: inline-block;
  }

  .search-box-container {
    width: 100%;
  }

  .group-rows:not(.ratings) {
    gap: 0.20rem 1rem;
  }

  .ratings .group-rows {
    gap: 1rem;
  }

  .carousel-container-trips,
  .carousel__item {
    width: 100%;
    min-width: 100px;
    padding: 0 0 2rem;
  }

  .carousel__icon {
    background-color: red;
    display: none;
  }

  .card-item {
    padding: 1rem 1rem;
  }

  .can-hidden {
    display: none;
  }
}

:root {
  /* Colors Light Mode */
  --color-terracotta: #B84446;
  --color-burgundy: #9a3739;
  --color-jungle: #37633F;
  --color-feuille: #67944C;
  --color-espoir: #A3C651;
  --color-eclat: #EFE3C5;
  --color-warning: #ffe599;
  --color-amber: #F9CB5C;

  /* Grey scale */
  --color-grey-light: #f2f2f2;
  --color-grey-medium: #d6d6d6;
  --color-grey-dark: #4a4a4a;

  /* Pastel colors */
  --color-pastel-blue: #e3f2fd;
  --color-pastel-green: #d4edda;
  --color-pastel-yellow: #fff3cd;
  --color-pastel-red: #f8d7da;
  --color-pastel-grey: #e2e3e5;

  /* Solid colors */
  --color-blue: #1976d2;
  --color-green: var(--color-jungle);
  --color-yellow: var(--color-amber);
  --color-red: var(--color-burgundy);
  --color-grey-solid: var(--color-grey-dark);

  /* Border */
  --color-border: #ddd;

  /* Fonts */
  --font-family-title: 'Poppins', sans-serif;
  --font-family-text: 'Roboto', sans-serif;

  /* Text colors */
  --color-text-primary: #000000;
  --color-text-secondary: #666666;

  /* Shadows */
  --shadow-xs: 0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-sm: 0 4px 8px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06);
  --shadow-md: 0 6px 12px rgba(0, 0, 0, 0.10), 0 3px 6px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
  --shadow-xl: 0 14px 28px rgba(0, 0, 0, 0.14), 0 6px 10px rgba(0, 0, 0, 0.10);
  --shadow-inset: rgb(204, 219, 232) 3px 3px 6px 0px inset, rgba(255, 255, 255, 0.5) -3px -3px 6px 1px inset;
  --shadow-avatar-active: rgba(0, 0, 0, 0.3) 3px 3px 6px 0px inset, rgba(0, 0, 0, 0.2) -3px -3px 6px 1px inset;
  --shadow-input: 0 0 0 3px rgba(102, 126, 234, 0.1);

  /* Backgrounds */
  --bg-primary: #FFFFFF;
  --bg-primary-alpha: rgba(255, 255, 255, 0.6);
  --bg-black-alpha: rgba(0, 0, 0, 0.05);

  /* Border radius */
  --border-radius-xsm: 2px;
  --border-radius-sm: 12px;
  --border-radius-md: 16px;
  --border-radius-lg: 22px;

  /* --------------- Admin Dashboard --------------- */

  /* Admin colors */
  --admin-bg: #f5f5f5;
  --admin-card-bg: #FFFFFF;
  --admin-bg-soft: #f9f9f9;
  --admin-bg-hover: #fafafa;
  --admin-border: #ddd;
  --admin-border-light: #f0f0f0;
  --admin-border-medium: #e0e0e0;
  --admin-border-light2: #e8e8e8;
  --admin-label-color: #666;
  --admin-muted: #999;
  --admin-warning: #ff9800;
  --admin-info: #007bff;
  --admin-link-hover: #0056b3;
  --admin-disabled-bg: #f5f5f5;
  --admin-preview-text: #555;
  --admin-stat-meta: #bbb;

  /* Admin shadows */
  --admin-shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.1);
  --admin-shadow-focus: 0 0 0 3px rgba(103, 148, 76, 0.1);
  --admin-card-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  --admin-card-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.1);
  --admin-save-shadow: 0 2px 4px rgba(103, 148, 76, 0.3);

  /* Admin hover backgrounds */
  --admin-danger-hover: rgba(184, 68, 70, 0.1);
  --admin-warning-hover: rgba(255, 152, 0, 0.1);
  --admin-info-hover: rgba(0, 123, 255, 0.1);
  --admin-edit-hover: rgba(55, 99, 63, 0.1);

  /* Admin error */
  --admin-error-bg: #fff5f5;
  --admin-error-border: #fdd;

  /* Badges light */
  --badge-pending-bg: #fff3cd;
  --badge-pending-text: #856404;
  --badge-paid-bg: #d4edda;
  --badge-paid-text: #155724;
  --badge-validated-bg: #d1ecf1;
  --badge-validated-text: #0c5460;
  --badge-refunded-bg: #e2e3e5;
  --badge-refunded-text: #383d41;
  --badge-cancelled-bg: #f8d7da;
  --badge-cancelled-text: #721c24;
  --badge-neutral-bg: #e2e3e5;
  --badge-neutral-text: #383d41;

  /* Roles light */
  --role-admin-bg: #dc3545;
  --role-driver-bg: #17a2b8;
  --role-user-bg: #6c757d;
  --role-default-bg: #e9ecef;
  --role-default-text: #495057;

}

[data-theme="dark"] {
  /* Colors Dark Mode */
  --color-terracotta: #D66668;
  --color-burgundy: #B84446;
  --color-eclat: #3a3525;
  --color-jungle: #5a9866;
  --color-feuille: #7faa6a;
  --color-espoir: #b5d678;
  --color-warning: #665c3d;
  --color-amber: #c99e3e;

  /* Grey scale */
  --color-grey-light: #2A2A2A;
  --color-grey-medium: #3A3A3A;
  --color-grey-dark: #B5B5B5;

  /* Pastel colors */
  --color-pastel-blue: #1a3a4d;
  --color-pastel-green: #1e3a25;
  --color-pastel-yellow: #3d3a28;
  --color-pastel-red: #3d2426;
  --color-pastel-grey: #2d2d2d;

  /* Solid colors */
  --color-blue: #4a9eff;
  --color-green: #5cb85c;
  --color-yellow: #ffd54f;
  --color-red: #ef5350;
  --color-grey-solid: #7a7a7a;

  /* Border */
  --color-border: #444;

  /* Text colors */
  --color-text-primary: #e0e0e0;
  --color-text-secondary: #a0a0a0;


  /* Shadows (dark mode = plus soft, plus diffuses) */
  --shadow-xs: 0 2px 4px rgba(0, 0, 0, 0.4);
  --shadow-sm: 0 4px 8px rgba(0, 0, 0, 0.45);
  --shadow-md: 0 6px 14px rgba(0, 0, 0, 0.5);
  --shadow-lg: 0 8px 20px rgba(0, 0, 0, 0.55);
  --shadow-xl: 0 14px 32px rgba(0, 0, 0, 0.6);
  --shadow-inset: inset 2px 2px 4px rgba(0, 0, 0, 0.6), inset -2px -2px 4px rgba(255, 255, 255, 0.04);
  --shadow-avatar-active: inset 3px 3px 6px rgba(0, 0, 0, 0.7), inset -3px -3px 6px rgba(255, 255, 255, 0.05);
  --shadow-input: 0 0 0 3px rgba(100, 181, 246, 0.25);

  /* Backgrounds */
  --bg-primary: #1a1a1a;
  --bg-primary-alpha: rgba(26, 26, 26, 0.6);
  --bg-black-alpha: rgba(255, 255, 255, 0.05);

  /* --------------- Admin Dashboard --------------- */

  /* Admin colors */
  --admin-bg: #1e1e1e;
  --admin-card-bg: #2a2a2a;
  --admin-bg-soft: #252525;
  --admin-bg-hover: #2f2f2f;
  --admin-border: #444;
  --admin-border-light: #333;
  --admin-border-medium: #3a3a3a;
  --admin-border-light2: #383838;
  --admin-label-color: #a0a0a0;
  --admin-muted: #777;
  --admin-warning: #ff9800;
  --admin-info: #42a5f5;
  --admin-link-hover: #64b5f6;
  --admin-disabled-bg: #2a2a2a;
  --admin-preview-text: #b0b0b0;
  --admin-stat-meta: #666;

  /* Admin shadows */
  --admin-shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.3);
  --admin-shadow-focus: 0 0 0 3px rgba(103, 148, 76, 0.2);
  --admin-card-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  --admin-card-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.3);
  --admin-save-shadow: 0 2px 4px rgba(103, 148, 76, 0.4);

  /* Admin hover backgrounds */
  --admin-danger-hover: rgba(214, 102, 104, 0.15);
  --admin-warning-hover: rgba(255, 152, 0, 0.15);
  --admin-info-hover: rgba(66, 165, 245, 0.15);
  --admin-edit-hover: rgba(90, 152, 102, 0.15);

  /* Admin error */
  --admin-error-bg: #3a2222;
  --admin-error-border: #5a3333;

  /* Badges dark */
  --badge-pending-bg: #4a3f1a;
  --badge-pending-text: #ffd966;
  --badge-paid-bg: #1e3a25;
  --badge-paid-text: #81c784;
  --badge-validated-bg: #1a3a44;
  --badge-validated-text: #4dd0e1;
  --badge-refunded-bg: #3a3a3a;
  --badge-refunded-text: #b0b0b0;
  --badge-cancelled-bg: #3a2226;
  --badge-cancelled-text: #ef9a9a;

  /* Roles dark */
  --role-admin-bg: #c62828;
  --role-driver-bg: #00acc1;
  --role-user-bg: #757575;
  --role-default-bg: #3a3a3a;
  --role-default-text: #b0b0b0;
}
</style>
